import dayjs, { Dayjs } from "dayjs";
import { SaleDepositResponse } from "../../types/sale-deposit.type";
import {
  SaleCenterClosingStatus,
  SaleCenterClosingStatusFilter,
} from "../../types/sale-center-closing.type";
import { roundMoney } from "./transfer.utils";

/** The date shape the /sale-center-closings queries take. */
const API_DATE_FORMAT = "YYYY-MM-DD";

/** How a business date is shown to the user, matching the report table's วันที่ column. */
const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";

/** The สาขา filter's ทั้งหมด option — the one value that is not a branch code. */
const ALL_BRANCHES = "all";

/**
 * The three figures the ปิดศูนย์เซล panel names, shared by the supervisor's form and the
 * recorded outcome the rest of the review cohort reads: the same number must not be called
 * two different things depending on who is looking at it.
 */
export const CASH_COLLECTED_LABEL = "ยอดเงินสดรวมฝากเก็บ";
export const EXPENSE_LABEL = "ยอดค่าใช้จ่าย (DIP)";
export const SEND_BACK_LABEL = "ส่งกลับให้สาขาแก้ไข";

/**
 * Said beside ยอดที่ต้องโอนรวม on a day that has notes. The closing summary names the day's notes
 * (ex-VAT, cash and credit alike) but not which of them were cash, so this states what the
 * total includes without claiming that anything was deducted.
 */
export const NET_OF_CASH_NOTES_HINT = "รวมใบลดหนี้/ใบเพิ่มหนี้เงินสดแล้ว";

/** Stands in for a name or a time the server never recorded. */
export const MISSING_VALUE = "-";

/** Spelled out, for the ปิดศูนย์เซล panel where the chip is the only thing naming the domain. */
export const CLOSING_STATUS_LABEL: Record<SaleCenterClosingStatus, string> = {
  open: "ยังไม่ปิดศูนย์เซล",
  closed: "ปิดศูนย์เซลแล้ว",
  sent_back: "ส่งกลับให้สาขาแก้ไข",
};

/**
 * The same three states in the compact register the สถานะการปิดยอด column and its filter
 * need: the column header already says what is being closed, so repeating "ศูนย์เซล" in
 * twenty consecutive chips only makes them wider.
 */
export const CLOSING_STATUS_SHORT_LABEL: Record<SaleCenterClosingStatus, string> = {
  open: "ยังไม่ปิด",
  closed: "ปิดแล้ว",
  sent_back: "ส่งกลับแก้ไข",
};

/** The สถานะการปิดยอด filter's options, unfiltered first like every other filter here. */
export const CLOSING_STATUS_FILTER: {
  label: string;
  value: SaleCenterClosingStatusFilter;
}[] = [
  { label: "ทั้งหมด", value: "all" },
  { label: CLOSING_STATUS_SHORT_LABEL.open, value: "open" },
  { label: CLOSING_STATUS_SHORT_LABEL.closed, value: "closed" },
  { label: CLOSING_STATUS_SHORT_LABEL.sent_back, value: "sent_back" },
];

/** Guards the filter value carried in the query string, which anyone may hand-edit. */
export const isClosingStatusFilter = (
  value: string | null,
): value is SaleCenterClosingStatusFilter =>
  CLOSING_STATUS_FILTER.some((option) => option.value === value);

export const formatBusinessDate = (businessDate: string): string =>
  dayjs(businessDate).format(DISPLAY_DATE_FORMAT);

/** The one branch-day a closing can speak for. */
export interface ClosingBranchDay {
  branchCode: string;
  businessDate: string; // YYYY-MM-DD
}

interface ClosingBranchDayInput {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  /** Whether the signed-in user has a สาขา picker at all — head office and master do. */
  canSelectBranch: boolean;
  /** What that picker holds, `ALL_BRANCHES` included. Meaningless without the picker. */
  selectedBranch: string;
  /** The loaded page of the report, the only place a token-scoped user's branch is named. */
  rows: Pick<SaleDepositResponse, "branch_code">[];
}

/**
 * The branch-day the ปิดศูนย์เซล panel is allowed to speak for, or `null` when the filter is
 * wider than one.
 *
 * A closing covers a branch and a day; across a mix of branches or a range of days its figures
 * mean nothing — ยอดที่ต้องโอนรวม would be summed over branches nobody is closing, and the two
 * ตรวจแล้ว sign-offs would belong to different days. Showing nothing is the only honest answer
 * to a wider filter. This resolution is only needed where the scope is still up to the filter
 * row: the /report/deposit/:branchCode/:businessDate drill-down names its branch-day in the URL
 * and hands it to the table directly.
 *
 * Where the branch code comes from depends on who is asking. A user with the picker has said
 * which branch out loud, so the picker is authoritative and ทั้งหมด means "not one branch". A
 * user without the picker is pinned to their own branch by the JWT, and the rows on screen are
 * the only place that code is written down — the token's `branch_code` is not usable here
 * because head office carries one too (its own head-office branch) and it would name the wrong
 * one. Rows are read only on that token-scoped path, where every row is the user's own branch
 * whichever page is loaded.
 */
export const resolveClosingBranchDay = ({
  dateFrom,
  dateTo,
  canSelectBranch,
  selectedBranch,
  rows,
}: ClosingBranchDayInput): ClosingBranchDay | null => {
  if (!dateFrom.isSame(dateTo, "day")) return null;
  const businessDate = dateFrom.format(API_DATE_FORMAT);

  if (canSelectBranch) {
    return selectedBranch === ALL_BRANCHES
      ? null
      : { branchCode: selectedBranch, businessDate };
  }

  const branchCodes = rows
    .map((row) => row.branch_code)
    .filter((code): code is string => Boolean(code));
  const [firstBranchCode] = branchCodes;
  if (!firstBranchCode) return null;
  if (branchCodes.some((code) => code !== firstBranchCode)) return null;

  return { branchCode: firstBranchCode, businessDate };
};

/**
 * ยอดค่าใช้จ่าย (DIP) = ยอดเงินสดรวมฝากเก็บที่กรอก − ยอดที่ต้องโอนรวมของสาขานั้นวันนั้น.
 *
 * The two sides share a base, so the remainder is a real expense/shortfall rather than a units
 * mismatch: each deposit's `required_transfer_amount` is already
 * เงินสดในมือ + ยอดฝากเก็บ − ค่าใช้จ่ายรวม ± ใบลดหนี้/ใบเพิ่มหนี้เงินสด, i.e. ฝากเก็บ is inside it,
 * exactly as it is inside the cash figure head office counts.
 *
 * Negative is the normal case and must not be clamped: it is what the branch spent, and it is
 * the last number the supervisor reads before signing.
 */
export const closingExpenseAmount = (
  cashCollectedAmount: number,
  requiredTransferTotal: number,
): number => roundMoney(cashCollectedAmount - requiredTransferTotal);
